import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props

// components
import { CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../typography/text';
import { IPaginationResourceShort } from '../../../../../props/general';
import Pagination from '../../../../uiframeworks/pagination';
import { NavLink } from 'react-router-dom';
import PermissionsService from '../../../../../services/permissions';
import { IDepositResourceShortProps } from '../../../../../props/finance/incomes/deposits';
import DepositService from '../../../../../services/finance/deposits';
import GeneralService from '../../../../../services/general';
import CreateDepositForm from '../form';

interface DepositsListProps {
    qs?: string[];
    orderId?: string;
    deposits?: IDepositResourceShortProps[];
    columns?: TDepositsListColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TDepositsListColumn = 'user' | 'amount';

const DepositsList: React.FC<DepositsListProps> = (props: DepositsListProps) => {
    const shownColumns: TDepositsListColumn[] = props.columns || [
        'user',
        'amount'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [deposits, setDeposits] = React.useState<IDepositResourceShortProps[]>(props.deposits || []);
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "user",
            name: "Customer / Employee / Vendor",
            minWidth: 125,
            onRender: (item: IDepositResourceShortProps) => {
                let url: string = "";
                if (item.user) {
                    url = `/users/customers/${item.user.id}`; 
                } else if (item.vendor) {
                    url = `/operationals/vendors/${item.vendor.id}`;
                }

                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={url}>
                        {item.user ? <Text size={'small'}>{item.user.name}</Text> : null}
                        {item.vendor ? <Text size={'small'}>{item.vendor.name}</Text> : null}
                    </NavLink>
                </Stack>
            }
        },
        {
            key: "amount",
            name: "Amount",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IDepositResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(item.amount))}</Text>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.deposits) {
            _onRetrieveDeposits();
        } else {
            setLoaded(true);
        }
    }, [keyword]);

    const _onRetrieveDeposits = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = props.qs || [];
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const _results = await DepositService.retrieve(qs.join("&"));
            setDeposits(_results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_deposits_list_error',
                text: 'Failed to retrieve deposits list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                }
            });
        }

        const hasCreatePermission = PermissionsService.hasPermission(['deposits.transactions.create'], user.permissions);
        if (!props.hideCreateButton && hasCreatePermission) {
            farItems.push({
                key: "create",
                text: "Add Deposit",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Add Deposit"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('deposit.create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        setKeyword(value || "");
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);

        if (refresh) {
            _onRetrieveDeposits();
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']}>
                {
                    !loaded || (loaded && deposits.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={deposits}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TDepositsListColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && deposits.length < 1 ? <Stack styles={{ root: { padding: props.variant === 'plain' ? 0 : 10 } }}>
                    <Text>No deposits found in any vendors/users</Text>
                </Stack> : null}
            </Stack>
            {pagination && deposits.length > 0 ? <Pagination goToPage={_onRetrieveDeposits} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'deposit.create' ? <CreateDepositForm onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default DepositsList;
